import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// A user reading mid-history is unambiguously "not pinned"; an absolute px
// gap is steadier than a fractional-viewport heuristic during reflow.
const PIN_THRESHOLD_PX = 24;

interface UseScrollPinReturn {
  scrollRef: React.RefObject<HTMLDivElement>;
  showJumpToLatest: boolean;
  onScroll: () => void;
  jumpToLatest: () => void;
}

/**
 * Keep a scroll container following appended/streamed content while the user is
 * pinned to the bottom, and never yank a user who scrolled up to read history.
 * `followKey` should change whenever content that may grow the container does
 * (message count, the last message's streamed content) so the follow re-runs.
 * `resetKey` should change when the container swaps to a different thread (e.g.
 * live ↔ a past conversation); a change re-pins to the bottom of the new content
 * regardless of where the previous thread was scrolled.
 */
export default function useScrollPin(
  followKey: unknown,
  resetKey?: unknown
): UseScrollPinReturn {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Ref drives callbacks/effects; state drives the button render.
  const isPinnedRef = useRef(true);
  const prevResetKeyRef = useRef(resetKey);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const settleFrameRef = useRef<number | null>(null);

  const setPinned = useCallback((pinned: boolean) => {
    isPinnedRef.current = pinned;
    setShowJumpToLatest(!pinned);
  }, []);

  const computePinned = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_THRESHOLD_PX;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
    const el = scrollRef.current;
    if (!el?.scrollTo) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    // A single scroll lands short while height is still settling (history
    // reflow, a growing bubble), so re-clamp over a few frames. Instant only —
    // a smooth re-clamp would fight its own animation. Cancel any in-flight
    // chain first so a fast token stream can't stack overlapping loops.
    if (behavior !== "auto" || typeof requestAnimationFrame === "undefined") {
      return;
    }
    if (settleFrameRef.current !== null) {
      cancelAnimationFrame(settleFrameRef.current);
    }
    let frame = 0;
    const settle = () => {
      const node = scrollRef.current;
      if (!node?.scrollTo) return;
      node.scrollTo({ top: node.scrollHeight, behavior: "auto" });
      settleFrameRef.current =
        ++frame < 3 ? requestAnimationFrame(settle) : null;
    };
    settleFrameRef.current = requestAnimationFrame(settle);
  }, []);

  const onScroll = useCallback(() => {
    setPinned(computePinned());
  }, [computePinned, setPinned]);

  // Follow content only while pinned; useLayoutEffect scrolls before paint.
  // A resetKey change swaps threads, so re-pin to the bottom of the new content.
  useLayoutEffect(() => {
    if (prevResetKeyRef.current !== resetKey) {
      prevResetKeyRef.current = resetKey;
      setPinned(true);
      scrollToBottom("auto");
      return;
    }
    if (isPinnedRef.current) scrollToBottom("auto");
  }, [followKey, resetKey, scrollToBottom, setPinned]);

  const jumpToLatest = useCallback(() => {
    setPinned(true);
    // Instant, not smooth: a smooth animation fires intermediate scroll events
    // that read as "not at bottom" and flicker the button back mid-scroll.
    scrollToBottom("auto");
  }, [scrollToBottom, setPinned]);

  // Recompute pin state when the container resizes (e.g. the composer grows).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setPinned(computePinned()));
    observer.observe(el);
    return () => observer.disconnect();
  }, [computePinned, setPinned]);

  return { scrollRef, showJumpToLatest, onScroll, jumpToLatest };
}
